import { useState, useEffect } from 'react'
import { Trash2, UserPlus, Users, Mail, Shield } from 'lucide-react'
import { adminApi } from '../../lib/api'
import { useToast } from '../ui/ToastProvider'

interface TripParticipant {
  id: string
  userId: string
  userName: string
  userEmail?: string
  userAvatar?: string
  role: 'owner' | 'admin' | 'member'
  joinedAt: string
  status: 'active' | 'invited' | 'left'
}

interface ParticipantsTabProps {
  tripId: string
  preloadedData?: any[]
}

export default function ParticipantsTab({ tripId, preloadedData }: ParticipantsTabProps) {
  const { showToast } = useToast()
  const [participants, setParticipants] = useState<TripParticipant[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    userEmail: '',
    role: 'member' as TripParticipant['role'],
  })

  useEffect(() => {
    if (preloadedData && preloadedData.length >= 0) {
      const participants = preloadedData.map((p: any) => ({
        id: p.id,
        userId: p.user_id || p.userId,
        userName: p.user_name || p.userName || p.user?.name || 'Unknown',
        userEmail: p.user_email || p.userEmail || p.user?.email,
        userAvatar: p.user_avatar || p.userAvatar || p.user?.avatar_url,
        role: p.role || 'member',
        joinedAt: p.joined_at || p.joinedAt || p.created_at,
        status: p.status || 'active',
      }))
      setParticipants(participants)
      setLoading(false)
    } else {
      loadParticipants()
    }
  }, [tripId, preloadedData])

  const loadParticipants = async () => {
    setLoading(true)
    try {
      const response = await adminApi.getTripParticipants(tripId)
      const rawData = response.data?.data || response.data || []
      const participants = Array.isArray(rawData) ? rawData.map((p: any) => ({
        id: p.id,
        userId: p.user_id || p.userId,
        userName: p.user_name || p.userName || p.user?.name || 'Unknown',
        userEmail: p.user_email || p.userEmail || p.user?.email,
        userAvatar: p.user_avatar || p.userAvatar || p.user?.avatar_url,
        role: p.role || 'member',
        joinedAt: p.joined_at || p.joinedAt || p.created_at,
        status: p.status || 'active',
      })) : []
      setParticipants(participants)
    } catch (error) {
      console.error('Failed to load participants:', error)
      setParticipants([])
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const dataToSave = {
        user_email: formData.userEmail,
        role: formData.role,
      }
      await adminApi.inviteTripParticipant(tripId, dataToSave)
      await loadParticipants()
      setFormData({ userEmail: '', role: 'member' })
      setShowForm(false)
      showToast('Participant invited successfully!', { type: 'success' })
    } catch (error) {
      console.error('Failed to invite participant:', error)
      showToast('Failed to invite participant. Please try again.', { type: 'error' })
    }
  }

  const handleRemove = async (participantId: string) => {
    if (!confirm('Are you sure you want to remove this participant?')) return
    try {
      await adminApi.removeTripParticipant(tripId, participantId)
      await loadParticipants()
    } catch (error) {
      console.error('Failed to remove participant:', error)
      showToast('Failed to remove participant. Please try again.', { type: 'error' })
    }
  }

  const handleUpdateRole = async (participantId: string, newRole: TripParticipant['role']) => {
    try {
      await adminApi.updateTripParticipantRole(tripId, participantId, { role: newRole })
      await loadParticipants()
    } catch (error) {
      console.error('Failed to update role:', error)
      showToast('Failed to update role. Please try again.', { type: 'error' })
    }
  }

  const getRoleBadge = (role: TripParticipant['role']) => {
    switch (role) {
      case 'owner':
        return <span className="badge badge-success">Owner</span>
      case 'admin':
        return <span className="badge badge-info">Admin</span>
      case 'member':
        return <span className="badge badge-secondary">Member</span>
    }
  }

  const getStatusBadge = (status: TripParticipant['status']) => {
    switch (status) {
      case 'active':
        return <span className="text-xs text-green-600">Active</span>
      case 'invited':
        return <span className="text-xs text-yellow-600">Invited</span>
      case 'left':
        return <span className="text-xs text-gray-600">Left</span>
    }
  }

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading participants...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Trip Participants</h3>
          <p className="text-sm text-gray-600 mt-1">Manage who can access and edit this trip</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn-primary flex items-center gap-2"
        >
          <UserPlus className="h-4 w-4" />
          Invite Participant
        </button>
      </div>

      {/* Invite Form */}
      {showForm && (
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <h4 className="text-md font-semibold text-gray-900 mb-4">Invite Participant</h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  value={formData.userEmail}
                  onChange={(e) => setFormData({ ...formData, userEmail: e.target.value })}
                  className="input w-full pl-10"
                  placeholder="user@example.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as TripParticipant['role'] })}
                className="input w-full"
              >
                <option value="member">Member - Can view and comment</option>
                <option value="admin">Admin - Can edit and manage</option>
              </select>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                An invitation will be sent to this email address. They'll need to accept before joining.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false)
                  setFormData({ userEmail: '', role: 'member' })
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary flex-1"
              >
                Send Invitation
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Participants List */}
      {participants.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No participants yet</p>
          <p className="text-sm text-gray-500 mt-1">Invite others to join and collaborate on this trip</p>
        </div>
      ) : (
        <div className="space-y-2">
          {participants.map((participant) => (
            <div
              key={participant.id}
              className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  {participant.userAvatar ? (
                    <img
                      src={participant.userAvatar}
                      alt={participant.userName}
                      className="h-12 w-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-600 font-semibold text-lg">
                        {participant.userName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-gray-900">{participant.userName}</h4>
                    {getRoleBadge(participant.role)}
                    {getStatusBadge(participant.status)}
                  </div>
                  {participant.userEmail && (
                    <p className="text-sm text-gray-600">{participant.userEmail}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Joined {new Date(participant.joinedAt).toLocaleDateString()}
                  </p>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {participant.role !== 'owner' && (
                    <>
                      <select
                        value={participant.role}
                        onChange={(e) => handleUpdateRole(participant.id, e.target.value as TripParticipant['role'])}
                        className="input text-sm py-1 px-2"
                        disabled={participant.status !== 'active'}
                      >
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                      </select>
                      <button
                        onClick={() => handleRemove(participant.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title="Remove"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  {participant.role === 'owner' && (
                    <div className="flex items-center gap-1 text-sm text-gray-500 px-3">
                      <Shield className="h-4 w-4" />
                      <span>Owner</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-yellow-900 mb-2">Participant Roles</h4>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li><strong>Owner:</strong> Full control over the trip, cannot be removed</li>
          <li><strong>Admin:</strong> Can edit trip details, manage events, and invite participants</li>
          <li><strong>Member:</strong> Can view trip details and leave comments</li>
        </ul>
      </div>
    </div>
  )
}
